import * as React from 'react';
import ToggleButton from 'ui/ToggleButton/ToggleButton';
import Tabs from '@mui/material/Tabs';

import Typography from '@mui/material/Typography';
import { Link, useLocation } from 'react-router-dom';

import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import StyActionContainer from 'ui/StyledComponents/StyActionContainer';
import { useHasTabBeenVisited } from 'routes/BudgetModule/components/shared/TabulatedChipButtonGroup/hooks';

const StyGridHeader = styled(Box, {
    name: 'StyGridHeader',
})({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    padding: '12px 6px 12px 6px',
    backgroundColor: 'white',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
});

const StyTabs = styled(Tabs, {
    name: 'StyTabs',
})({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'center',
    marginRight: '4rem',
    marginLeft: 20,
    '& .MuiTabs-indicator': {
        backgroundColor: 'transparent',
    },
});

const StyToggleButton = styled(ToggleButton, { name: 'StyToggleButton' })<
    React.ComponentProps<typeof Link> &
        React.ComponentProps<typeof ToggleButton>
>(({ theme }) => ({
    padding: '8px 11px',
    backgroundColor: 'whitesmoke',
    alignItems: 'center',
    marginRight: '8px',
    '&.Mui-selected:hover': {
        backgroundColor: theme.palette.primary.main,
    },
    '&.Mui-disabled body1SemiBold': {
        color: theme.palette.grey[500],
    },
}));

type TabT = {
    label: string;
    key: string | number;
    children: React.ReactNode;
    pathname: string;
    disabled?: boolean;
    tabActions?: React.ReactNode;
};

// if keepSearch set to False:
//    - click on tabs will reset the search to empty string
//    - search will not be part of the front-end cache (children will not be remounted on change to search)
type Props = {
    tabsList: TabT[];
    className?: string;
    onTabClicked?: (arg1: TabT) => void;
    keepSearch?: boolean;
};

// TODO : Merge with TabulatedChipButtonGroup.tsx, and probably TabulatedPage.tsx
export function TabulatedChipButtonGroupBudget(
    props: Props,
): React.ReactElement {
    const { tabsList, className, onTabClicked, keepSearch = true } = props;
    const location = useLocation();

    const isTabVisible = (tab: TabT) => location.pathname === tab.pathname;
    const hasTabBeenVisited = useHasTabBeenVisited(tabsList, keepSearch);

    const onClick = (tab: TabT) => {
        if (onTabClicked && !isTabVisible(tab)) {
            onTabClicked(tab);
        }
    };

    return (
        <Box className={className}>
            <StyGridHeader id="tabulated-chip-button-group-header">
                <StyTabs
                    value={location.pathname}
                    sx={{ indicator: 'transparent' }}
                >
                    {tabsList.map((tab) => (
                        <StyToggleButton
                            to={{
                                pathname: tab.pathname,
                                search: keepSearch ? location.search : '',
                            }}
                            state={location.state}
                            disabled={tab.disabled}
                            key={tab.label}
                            value={tab.pathname}
                            disableRipple
                            selected={isTabVisible(tab)}
                            LinkComponent={Link}
                            onClick={() => onClick(tab)}
                        >
                            <Typography
                                sx={{ fontSize: '0.9em', display: 'flex' }}
                                variant="body1SemiBold"
                            >
                                {tab.label}
                            </Typography>
                        </StyToggleButton>
                    ))}
                </StyTabs>
                <StyActionContainer sx={{ flex: 1 }}>
                    {tabsList.find(isTabVisible)?.tabActions}
                </StyActionContainer>
            </StyGridHeader>
            {tabsList.map((tab) => (
                <Box key={tab.pathname} hidden={!isTabVisible(tab)}>
                    {/* NOTE optimization: if the tab has already been visited, let's keep it in the DOM
                     in order to not unmount it */}

                    {hasTabBeenVisited[
                        tab.pathname + (keepSearch ? location.search : '')
                    ] && tab.children}
                </Box>
            ))}
        </Box>
    );
}
